import React, { useState } from 'react';
import { ArrowLeft, Plus, Search, Filter, Calendar, Users, DollarSign, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ProjetoSocial {
  id: string;
  nome: string;
  descricao: string;
  categoria: 'assistencia' | 'educacao' | 'saude' | 'habitacao' | 'alimentacao' | 'capacitacao';
  valor: number;
  dataInicio: string;
  dataFim?: string;
  responsavel: string;
  beneficiarios: number;
  status: 'planejamento' | 'em_andamento' | 'concluido' | 'suspenso';
  localizacao: string;
  observacoes?: string;
}

const ProjetosSociaisPage: React.FC = () => {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState({
    nome: '',
    categoria: '',
    status: '',
    responsavel: '',
    dataInicio: '',
    dataFim: ''
  });

  // Dados mockados
  const [projetos, setProjetos] = useState<ProjetoSocial[]>([
    {
      id: '1',
      nome: 'Cesta Básica Solidária',
      descricao: 'Distribuição mensal de cestas básicas para famílias carentes',
      categoria: 'alimentacao',
      valor: 5000.00,
      dataInicio: '2024-01-01',
      responsavel: 'Maria Silva',
      beneficiarios: 50,
      status: 'em_andamento',
      localizacao: 'Bairro Centro',
      observacoes: 'Projeto permanente da igreja'
    },
    {
      id: '2',
      nome: 'Curso de Informática',
      descricao: 'Curso básico de informática para jovens da comunidade',
      categoria: 'capacitacao',
      valor: 3500.00,
      dataInicio: '2024-03-01',
      dataFim: '2024-06-30',
      responsavel: 'João Santos',
      beneficiarios: 25,
      status: 'concluido',
      localizacao: 'Sala de informática da igreja',
      observacoes: 'Parceria com escola técnica local'
    },
    {
      id: '3',
      nome: 'Reforma Casa da Viúva',
      descricao: 'Reforma da casa de uma viúva da congregação',
      categoria: 'habitacao',
      valor: 8000.00,
      dataInicio: '2024-11-01',
      dataFim: '2024-12-15',
      responsavel: 'Pedro Costa',
      beneficiarios: 1,
      status: 'planejamento',
      localizacao: 'Rua das Flores, 123',
      observacoes: 'Mutirão aos sábados'
    }
  ]);

  const totalInvestido = projetos.reduce((acc, proj) => acc + proj.valor, 0);
  const totalBeneficiarios = projetos.reduce((acc, proj) => acc + proj.beneficiarios, 0);
  const projetosAtivos = projetos.filter(p => p.status === 'em_andamento').length;
  const projetosConcluidos = projetos.filter(p => p.status === 'concluido').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planejamento': return 'bg-blue-600 dark:bg-blue-500';
      case 'em_andamento': return 'bg-yellow-600 dark:bg-yellow-500';
      case 'concluido': return 'bg-green-600 dark:bg-green-500';
      case 'suspenso': return 'bg-destructive';
      default: return 'bg-muted-foreground';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'planejamento': return 'Planejamento';
      case 'em_andamento': return 'Em Andamento';
      case 'concluido': return 'Concluído';
      case 'suspenso': return 'Suspenso';
      default: return status;
    }
  };

  const handleStatusClick = (id: string) => {
    setProjetos(prev => prev.map(projeto => {
      if (projeto.id === id) {
        const statusOrder: ProjetoSocial['status'][] = ['planejamento', 'em_andamento', 'concluido', 'suspenso'];
        const currentIndex = statusOrder.indexOf(projeto.status);
        const nextIndex = (currentIndex + 1) % statusOrder.length;
        return { ...projeto, status: statusOrder[nextIndex] };
      }
      return projeto;
    }));
  };

  const getCategoriaColor = (categoria: string) => {
    switch (categoria) {
      case 'assistencia': return 'bg-purple-600 dark:bg-purple-500';
      case 'educacao': return 'bg-blue-600 dark:bg-blue-500';
      case 'saude': return 'bg-red-600 dark:bg-red-500';
      case 'habitacao': return 'bg-orange-600 dark:bg-orange-500';
      case 'alimentacao': return 'bg-green-600 dark:bg-green-500';
      case 'capacitacao': return 'bg-cyan-600 dark:bg-cyan-500';
      default: return 'bg-muted-foreground';
    }
  };

  const getCategoriaText = (categoria: string) => {
    switch (categoria) {
      case 'assistencia': return 'Assistência Social';
      case 'educacao': return 'Educação';
      case 'saude': return 'Saúde';
      case 'habitacao': return 'Habitação';
      case 'alimentacao': return 'Alimentação';
      case 'capacitacao': return 'Capacitação';
      default: return categoria;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/financas/administracao')}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Projetos Sociais</h1>
              <p className="text-muted-foreground">Gestão de projetos sociais da igreja</p>
            </div>
          </div>
          <Button onClick={() => navigate('/financas/administracao/projetos-sociais/cadastrar')} className="bg-primary hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-2" />
            Novo Projeto
          </Button>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Investido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-2xl font-bold text-blue-500">
                  R$ {totalInvestido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Beneficiários</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Heart className="h-4 w-4 text-red-500 mr-2" />
                <span className="text-2xl font-bold text-red-500">{totalBeneficiarios}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Projetos Ativos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="h-4 w-4 text-yellow-500 mr-2" />
                <span className="text-2xl font-bold text-yellow-500">{projetosAtivos}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Concluídos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-2xl font-bold text-green-500">{projetosConcluidos}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros e Tabela Unificados */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Projetos Sociais Cadastrados ({projetos.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Seção de Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 bg-muted/20 rounded-lg">
              <div>
                <Label htmlFor="filtro-nome">Nome do Projeto</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="filtro-nome"
                    placeholder="Buscar..."
                    value={filtros.nome}
                    onChange={(e) => setFiltros({...filtros, nome: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="filtro-categoria">Categoria</Label>
                <Select value={filtros.categoria} onValueChange={(value) => setFiltros({...filtros, categoria: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="assistencia">Assistência Social</SelectItem>
                    <SelectItem value="educacao">Educação</SelectItem>
                    <SelectItem value="saude">Saúde</SelectItem>
                    <SelectItem value="habitacao">Habitação</SelectItem>
                    <SelectItem value="alimentacao">Alimentação</SelectItem>
                    <SelectItem value="capacitacao">Capacitação</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filtro-status">Status</Label>
                <Select value={filtros.status} onValueChange={(value) => setFiltros({...filtros, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="planejamento">Planejamento</SelectItem>
                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                    <SelectItem value="concluido">Concluído</SelectItem>
                    <SelectItem value="suspenso">Suspenso</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filtro-responsavel">Responsável</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="filtro-responsavel"
                    placeholder="Buscar..."
                    value={filtros.responsavel}
                    onChange={(e) => setFiltros({...filtros, responsavel: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="filtro-data-inicio">Data Inicial</Label>
                <Input
                  id="filtro-data-inicio"
                  type="date"
                  value={filtros.dataInicio}
                  onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="filtro-data-fim">Data Final</Label>
                <Input
                  id="filtro-data-fim"
                  type="date"
                  value={filtros.dataFim}
                  onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
                />
              </div>
            </div>

            {/* Tabela de Projetos */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">PROJETO</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">CATEGORIA</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">VALOR</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">PERÍODO</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">RESPONSÁVEL</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">BENEFICIÁRIOS</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {projetos.map((projeto) => (
                    <tr key={projeto.id} className="border-b">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium">{projeto.nome}</div>
                          <div className="text-sm text-muted-foreground">{projeto.descricao}</div>
                          <div className="text-sm text-muted-foreground">{projeto.localizacao}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={`${getCategoriaColor(projeto.categoria)} text-white`}>
                          {getCategoriaText(projeto.categoria)}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 font-medium">
                        R$ {projeto.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                          <div>
                            <div>{new Date(projeto.dataInicio).toLocaleDateString('pt-BR')}</div>
                            {projeto.dataFim && (
                              <div className="text-sm text-muted-foreground">
                                até {new Date(projeto.dataFim).toLocaleDateString('pt-BR')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-muted-foreground mr-2" />
                          <span>{projeto.responsavel}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <Heart className="h-4 w-4 text-destructive mr-2" />
                          <span className="font-medium">{projeto.beneficiarios}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge 
                          className={`${getStatusColor(projeto.status)} text-white cursor-pointer relative group`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleStatusClick(projeto.id);
                          }}
                          title="Clique para mudar status"
                        >
                          {getStatusText(projeto.status)}
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Clique para mudar status
                          </span>
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>


      </div>
    </div>
  );
};

export default ProjetosSociaisPage;
export { ProjetosSociaisPage };